// frontend/src/lib/wizardDataSource.ts
// Injectable data source for the (shared) ApplicationWizard. Lets the single
// wizard component power BOTH the initial /apply flow and the renewal /renew flow
// without forking — the wizard calls dataSource.<m>() instead of a hardcoded
// api namespace. Plan: F-027 (2026-06-18-grant-renewal-wizard, Task 7).
import type { QueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/services/api';
import type { FamilyApplication, ApplicationDocumentMeta } from '@/types/applications';
import type { ApiResponse } from '@/types/api';
import type { ResumeStepResult } from '@/services/api';

export interface WizardDataSource {
  getMine: () => Promise<ApiResponse<FamilyApplication>>;
  createDraft: () => Promise<ApiResponse<FamilyApplication>>;
  saveSection: (id: string, section: string, values: Record<string, unknown>) => Promise<ApiResponse<FamilyApplication>>;
  /**
   * #2756 item 1: the save sent as the page is hidden for good (pagehide). A
   * keepalive request the browser lets finish after a desktop tab closes.
   * Optional: a source without it falls back to `saveSection`.
   */
  saveSectionOnUnload?: (id: string, section: string, values: Record<string, unknown>) => Promise<ApiResponse<FamilyApplication>>;
  /**
   * #2440: record the step the family is ON, so a sign-out, refresh or Back
   * reopens there. Optional so a test or preview source without it simply
   * resumes from where the data sits, as before.
   */
  saveResumeStep?: (id: string, step: string) => Promise<ApiResponse<ResumeStepResult>>;
  /**
   * `payload.version_n` is the definition version the wizard rendered (#1095
   * item 1). Both flows send it: the server checks the answers against that
   * version's required set and date rules (#2743 follow-up for renewals).
   * Optional on the seam so a test or preview source may omit it.
   */
  submit: (id: string, payload?: { version_n: number }) => Promise<ApiResponse<FamilyApplication>>;
  uploadDocument: (
    id: string,
    formData: FormData,
    onProgress?: (percent: number) => void,
  ) => Promise<ApiResponse<ApplicationDocumentMeta>>;
  listDocuments: (id: string) => Promise<ApiResponse<ApplicationDocumentMeta[]>>;
  /** #2471: view, remove and undo-remove the family's own documents. Optional
   *  so a test or preview source without them renders a roster with no
   *  controls rather than buttons with nothing behind them. */
  documentFile?: (id: string, docId: string) => Promise<Blob>;
  removeDocument?: (id: string, docId: string) => Promise<ApiResponse<{ id: string; kind: string }>>;
  restoreDocument?: (id: string, docId: string) => Promise<ApiResponse<{ id: string; kind: string }>>;
  /**
   * D-FB-12: upload the answer to a `file` field. Optional on the seam because
   * only the applications flow has the route — renewals carry no published
   * definition of their own, so a file question cannot reach that wizard.
   * Absent here means the control renders without a picker rather than
   * offering a button with nothing behind it.
   */
  /**
   * #2332: the react-query key family pages read this record under, when one
   * exists. A wizard mutation that changes the record's status writes its
   * result here (`syncMineCache`), so a page served from cache — the global
   * staleTime is 5 minutes — never offers a step the family already took.
   * Absent for the applications flow: no family page caches that read.
   */
  mineQueryKey?: readonly unknown[];
  uploadFieldDocument?: (
    id: string,
    fieldId: string,
    formData: FormData,
  ) => Promise<ApiResponse<{ id: string; field_id: string; filename: string }>>;
}

export const applicationsDataSource: WizardDataSource = {
  getMine: () => api.applications.getMine(),
  createDraft: () => api.applications.createDraft(),
  saveSection: (id, section, values) => api.applications.saveSection(id, section, values),
  saveSectionOnUnload: (id, section, values) => api.applications.saveSectionOnUnload(id, section, values),
  saveResumeStep: (id, step) => api.applications.saveResumeStep(id, step),
  submit: (id, payload) => api.applications.submit(id, payload),
  uploadDocument: (id, fd, onProgress) => api.applications.uploadDocument(id, fd, onProgress),
  listDocuments: (id) => api.applications.listDocuments(id),
  documentFile: (id, docId) => api.applications.documentFile(id, docId),
  removeDocument: (id, docId) => api.applications.removeDocument(id, docId),
  restoreDocument: (id, docId) => api.applications.restoreDocument(id, docId),
  uploadFieldDocument: (id, fieldId, fd) => api.applications.uploadFieldDocument(id, fieldId, fd),
};

/**
 * The family's own renewal, as `/bills` and the parent home read it. One
 * constant so the wizard that changes the renewal and the pages that show it
 * cannot drift onto different keys (#2332).
 */
export const RENEWAL_MINE_QUERY_KEY = ['renewal-mine'] as const;

export const renewalsDataSource: WizardDataSource = {
  mineQueryKey: RENEWAL_MINE_QUERY_KEY,
  getMine: () => api.renewals.getMine(),
  createDraft: () => api.renewals.createDraft(),
  saveSection: (id, section, values) => api.renewals.saveSection(id, section, values),
  saveSectionOnUnload: (id, section, values) => api.renewals.saveSectionOnUnload(id, section, values),
  saveResumeStep: (id, step) => api.renewals.saveResumeStep(id, step),
  // #2743 follow-up: the renewal wizard renders the published application, so
  // its submit carries the version it rendered, as the applications flow
  // does. POST /api/renewals/:id/submit holds the answers to THAT version's
  // required set (the #1095 ruling), not to one published after mount. The
  // body is built here because `api.renewals.submit` takes no payload.
  submit: async (id, payload) => {
    const response = await apiClient.post<ApiResponse<FamilyApplication>>(`/renewals/${id}/submit`, payload);
    return response.data;
  },
  uploadDocument: (id, fd, onProgress) => api.renewals.uploadDocument(id, fd, onProgress),
  listDocuments: (id) => api.renewals.listDocuments(id),
  documentFile: (id, docId) => api.renewals.documentFile(id, docId),
  removeDocument: (id, docId) => api.renewals.removeDocument(id, docId),
  restoreDocument: (id, docId) => api.renewals.restoreDocument(id, docId),
};

/**
 * #2332: after a wizard mutation that changes the record's status (creating
 * the draft, submitting it), put the server's answer into the cache family
 * pages read, and mark it stale so their next mount confirms it.
 *
 * Writing the answer — not only invalidating — is what keeps `/bills` honest
 * on the very first paint after submit: an invalidated entry still serves its
 * old value (an editable draft, "Continue your renewal") while the refetch is
 * in flight. The submit response is the same projected record `getMine`
 * returns, so it is a faithful stand-in until the refetch lands.
 */
export async function syncMineCache(
  queryClient: QueryClient,
  source: WizardDataSource,
  res: ApiResponse<FamilyApplication> | undefined,
): Promise<void> {
  const key = source.mineQueryKey;
  if (!key) return;
  if (res?.data) queryClient.setQueryData(key, res);
  await queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' });
}
